import { useBalanceByCurrency } from '@src/hooks/useBalanceByCurrency';
import {
  setSwapFromQuantity,
  toBigWithDefault,
  useCallbackRef,
  useSwapDependencies,
  useWLOrderFormDispatch,
  useWLOrderFormSelector,
} from '@talos/kyoko';
import { AnimatePresence } from 'framer-motion';
import { Fragment, useCallback, useMemo } from 'react';
import { Route, Routes } from 'react-router-dom';
import { InlineFormattedNumber } from '../../components/InlineFormattedNumber';
import { Input } from '../../components/Input';

import { GCashButton } from '@src/components/GCashButton';
import { AnimatedStackOutlet } from '../../components/AnimatedStackOutlet';
import { StackScreen } from '../../components/StackScreen';
import { CashierOverlay } from './CashierOverlay';
import { CashTransactionResultPage } from './CashTransactionResult';
import { useSendBalanceTransaction } from './useSendBalanceTransaction';

export const CashTransactionRoute = () => {
  return (
    <Routes>
      <Route path="*" element={<CashTransactionLayout />}>
        <Route
          path="confirmation"
          element={
            <StackScreen initialTitle="GCrypto+" canGoBack={false}>
              <CashTransactionResultPage />
            </StackScreen>
          }
        />
      </Route>
    </Routes>
  );
};
const CashTransactionLayout = () => {
  return (
    <>
      <AnimatedStackOutlet />
      <div className="flex size-full flex-1 grow">
        {/* Base background route: always rendered */}
        <CashTransactionPage />
        {/* Stack routes (like /Topup/verify) */}
      </div>
    </>
  );
};

export const CashTransactionPage = () => {
  useSwapDependencies();

  const dispatch = useWLOrderFormDispatch();
  const fromCurrency = useWLOrderFormSelector(state => state.swap.form.fromCurrency);
  const fromQty = useWLOrderFormSelector(state => state.swap.form.fromQty);
  const swapStep = useWLOrderFormSelector(state => state.swap.meta.step);
  const mode = useWLOrderFormSelector(state => state.swap.mode);

  const sendBalanceTransaction = useSendBalanceTransaction();

  const onClickContinueButton = useCallback(async () => {
    sendBalanceTransaction();
  }, [sendBalanceTransaction]);
  const balanceByCurrency = useBalanceByCurrency();

  const fromCurrencyBalance = fromCurrency.value?.Symbol
    ? balanceByCurrency?.get(fromCurrency.value?.Symbol)
    : undefined;

  const onHandleClickMaxButton = useCallbackRef(() => {
    if (fromCurrencyBalance?.Amount) {
      dispatch(setSwapFromQuantity({ qty: fromCurrencyBalance?.Amount, setTouched: true }));
    }
  });

  const { isConfirmDisabled, exceedsWithdrawBalance } = useMemo(() => {
    const qty = toBigWithDefault(fromQty.value, 0);
    const exceedsWithdrawBalance =
      mode === 'withdraw' && fromCurrencyBalance?.Amount ? qty.gt(fromCurrencyBalance.Amount) : false;
    const isDisabled = qty.eq(0) || exceedsWithdrawBalance;

    return { isConfirmDisabled: isDisabled, exceedsWithdrawBalance };
  }, [mode, fromQty.value, fromCurrencyBalance?.Amount]);

  return (
    <Fragment>
      <AnimatePresence>
        {/* The verify page holds the cashier overlay for withdrawal */}
        <AnimatePresence>{swapStep === 'sendingEntity' && <CashierOverlay />}</AnimatePresence>
      </AnimatePresence>
      <div
        className="shadow-depth-0 flex size-full flex-col items-center gap-4 p-4"
        data-testid="cash-transaction-page"
      >
        <div className="flex aspect-square size-14 flex-col items-center justify-center">
          <img alt="GCryptoSwap" src="/GCryptoSwap.svg" />
        </div>
        <div className="flex flex-col items-center">
          <h2 className="text-xl font-bold">{fromCurrency.value?.Symbol}</h2>
        </div>

        <div className="border-color-border bg-color-bg-main w-full rounded-md border p-4">
          <span className="text-color-text font-semibold">{fromCurrency.value?.Symbol}</span>
          <div className="w-full flex-col items-center gap-2">
            <Input
              onChange={qty => {
                dispatch(setSwapFromQuantity({ qty: qty ?? '', setTouched: true }));
              }}
              scale={fromQty.scale}
              value={fromQty.value}
              disabled={fromQty.isDisabled || swapStep === 'locked'}
              data-testid="swap-from-qty"
              error={exceedsWithdrawBalance ? 'Amount exceeds available balance' : undefined}
              inlineSuffix={
                mode === 'withdraw' &&
                fromCurrencyBalance && (
                  <button
                    onClick={onHandleClickMaxButton}
                    className="text-color-bg-primary mr-2 text-sm font-semibold"
                    data-testid="max-button"
                  >
                    Max
                  </button>
                )
              }
              suffix={
                mode === 'withdraw' && fromCurrencyBalance ? (
                  <div className="mr-auto flex gap-1 text-nowrap whitespace-nowrap">
                    {fromCurrency.value?.Description} Balance:{' '}
                    <InlineFormattedNumber
                      data-testid="swap-from-balance"
                      truncate={false}
                      currency={fromCurrency.value?.Symbol}
                      number={fromCurrencyBalance.Amount}
                    />
                  </div>
                ) : null
              }
            />
          </div>
        </div>

        <div className="mt-auto flex w-full flex-col gap-2 pt-2">
          <GCashButton data-testid="swap-button" size="lg" onClick={onClickContinueButton} disabled={isConfirmDisabled}>
            Confirm
          </GCashButton>
        </div>
      </div>
    </Fragment>
  );
};
